import Parse from 'parse'
import { getDisplayName } from '../utils/personName'

export interface UserForInvitation {
  id: string
  email: string
  username: string
  emailVerified: boolean
  createdAt: Date
  profile: {
    id: string
    firstName: string
    lastName: string
    /** Shown instead of first + last name when set (SC-438). */
    displayName?: string
    title: string
    avatar: string
    gameCharacter: string
    profileCompleted: boolean
    companyId: string | null
    customAvatarUrl?: string
  } | null
  isExistingMember: boolean
  displayName: string
}

export interface UserDetailsForInvitation extends UserForInvitation {
  existingCompanyUser: {
    id: string
    role: string
    permissions: string[]
    joinedAt: Date
    isActive: boolean
  } | null
  companyMemberships: Array<{
    companyId: string
    role: string
    permissions: string[]
    joinedAt: Date
    isActive: boolean
  }>
}

export interface UserSearchResult {
  success: boolean
  users: UserForInvitation[]
  total: number
  query: string
}

export interface UserDetailsResult {
  success: boolean
  user: UserDetailsForInvitation
}

class UserSearchService {
  /**
   * Search for existing users that can be invited to companies
   * @param query - Search query (name, email, username)
   * @param companyId - Company ID to check if user is already a member
   * @param limit - Maximum number of results
   * @returns Promise<UserSearchResult>
   */
  async searchUsersForInvitation(
    query: string, 
    companyId?: string, 
    limit: number = 20
  ): Promise<UserSearchResult> {
    try {
      const result = await Parse.Cloud.run('searchUsersForInvitation', {
        query,
        companyId,
        limit
      })
      
      return result
    } catch (error) {
      console.error('Error searching users for invitation:', error)
      throw error
    }
  }

  /**
   * Get detailed user information for invitation
   * @param userId - User ID to get details for
   * @param companyId - Company ID to check membership
   * @returns Promise<UserDetailsResult>
   */
  async getUserDetailsForInvitation(
    userId: string, 
    companyId?: string
  ): Promise<UserDetailsResult> {
    try {
      const result = await Parse.Cloud.run('getUserDetailsForInvitation', {
        userId,
        companyId
      })
      
      return result
    } catch (error) {
      console.error('Error getting user details for invitation:', error)
      throw error
    }
  }

  /**
   * Debounced search function for better performance
   * @param query - Search query
   * @param companyId - Company ID
   * @param limit - Maximum results
   * @param delay - Debounce delay in milliseconds
   * @returns Promise<UserSearchResult>
   */
  async debouncedSearch(
    query: string, 
    companyId?: string, 
    limit: number = 20, 
    delay: number = 300
  ): Promise<UserSearchResult> {
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(async () => {
        try {
          const result = await this.searchUsersForInvitation(query, companyId, limit)
          resolve(result)
        } catch (error) {
          reject(error)
        }
      }, delay)

      // Return a function to cancel the search
      return () => clearTimeout(timeoutId)
    })
  }

  /**
   * Format user display name
   * @param user - User object
   * @returns Formatted display name
   */
  formatDisplayName(user: UserForInvitation): string {
    return getDisplayName({ ...user.profile, username: user.username }, user.email)
  }

  /**
   * Check if user can be invited (not already a member)
   * @param user - User object
   * @returns boolean
   */
  canInviteUser(user: UserForInvitation): boolean {
    return !user.isExistingMember
  }

  /**
   * Get user status for invitation
   * @param user - User object
   * @returns Status information
   */
  getUserStatus(user: UserForInvitation) {
    return {
      isExistingMember: user.isExistingMember,
      emailVerified: user.emailVerified,
      profileCompleted: user.profile?.profileCompleted || false,
      hasCurrentCompany: !!user.profile?.companyId
    }
  }
}

// Export singleton instance
export const userSearchService = new UserSearchService()
export default userSearchService
